import { Box, Grid, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import PrimaryButton from "../../../../components/buttons/PrimaryButton";
import SecondaryButton from "../../../../components/buttons/SecondaryButton";
import LoadingIcon from "../../../../components/LoadingIcon";
import { ScreenshotAccordion } from "../../../../components/ScreenshotAccordion";
import { Toast } from "../../../../components/Toast";
import Guideline from "../components/Guideline";
import GuidelineStep from "../components/GuidelineStep";
import GuidelineStepByStep from "../components/GuidelineStepByStep";
import Torq1 from "./images/torq1.png";
import Torq2 from "./images/torq2.png";
import Torq3 from "./images/torq3.png";
import Torq4 from "./images/torq4.png";

import { useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import useAPI from "../../../../api/hooks/useAPI";
import useIsIntegrated from "../../../../api/hooks/useIsIntegrated";
import { integrationKeys } from "../../../../api/queryKeys";
import useExpandedList from "../../../../components/hooks/ExpandedList";
import { logos } from "../../environment/icons/iconExport";
import IntegrationGuideAlert from "../components/IntegrationGuideAlert";
import WebhookConfig from "../components/WebhookConfig";
import { IntegrationsMapping } from "../IntegrationsMapping";
import TorqConnectForm from "./TorqConnectForm";

const INTEGRATION_CODE_NAME = "torq";

const TorqIntegration = () => {
  const integration = IntegrationsMapping[INTEGRATION_CODE_NAME];
  const [searchParams] = useSearchParams();
  const [guideMode, setGuideMode] = useState(false);

  useEffect(() => {
    if (searchParams.has("viewonly")) {
      setGuideMode(true);
    }
  }, [searchParams]);

  const { isIntegrated, isIntegratedLoading } = useIsIntegrated(
    integration.codeName
  );
  const [loading, setLoading] = useState(false);
  const queryClient = useQueryClient();

  const { addToExpandedList, handleExpandAll, handleCollapseAll } =
    useExpandedList();
  const { registerTorqIntegration } = useAPI();

  const onSubmit = (values) => {
    setLoading(true);
    registerTorqIntegration(values.webhookURL.trim())
      .then((response) => {
        queryClient.invalidateQueries({
          queryKey: integrationKeys.all,
        });
        Toast.success(`${integration.displayName} integrated successfully`);
      })
      .catch((error) => {
        if (error.response) {
          Toast.error(`Something went wrong: ${error.response.data.detail}`);
        } else {
          Toast.error("An error occurred while processing the request.");
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Box>
      <Guideline
        title={
          isIntegrated
            ? ""
            : `Once integrated, the Breeze platform will send alerts about new detections to the ${integration.displayName} webhook.`
        }
        image={logos.TorqLogo}
        iconBorderRadius={0}
        name={integration.displayName}
        content={
          loading || isIntegratedLoading ? (
            <LoadingIcon size={25} />
          ) : isIntegrated && !guideMode ? (
            <WebhookConfig
              toolName={integration.codeName}
              toolDisplayName={integration.displayName}
            />
          ) : (
            <div>
              {/* BUTTONS TO CONTROL EXPANSION */}
              <Box mb="20px">
                <Grid container spacing={2} direction={"row"}>
                  {guideMode && <IntegrationGuideAlert />}

                  <Grid item>
                    <SecondaryButton onClick={handleExpandAll}>
                      Expand All Screenshots
                    </SecondaryButton>
                  </Grid>
                  <Grid item>
                    <SecondaryButton onClick={handleCollapseAll}>
                      Collapse All Screenshots
                    </SecondaryButton>
                  </Grid>
                </Grid>
              </Box>

              {/* SETUP INSTRUCTIONS */}
              <GuidelineStepByStep>
                <GuidelineStep title="Open Torq integration page and create a new webhook integration">
                  <Box style={{ display: "flex" }}>
                    <PrimaryButton
                      onClick={() => {
                        window.open(
                          "https://app.torq.io/integrations",
                          "_blank"
                        );
                      }}
                      style={{ textTransform: "none" }}
                    >
                      Open Torq
                    </PrimaryButton>
                  </Box>
                </GuidelineStep>
                <GuidelineStep title="Create new webhook integration">
                  <Typography variant="body1">
                    Click on Webhook and create a new integration called{" "}
                    <strong>Breeze</strong>
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: Torq1,
                        altext: "Create new application",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant="body1">
                    Fill in the details and click add.{""}
                    <br />
                    Once the webhook is registered, note down the URL and add it
                    in the <a href="#connectTorq">form below.</a>
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: Torq2,
                        altext: "Fill in the details and click Register",
                      },
                      {
                        file: Torq3,
                        altext: "Fill in the details and click Register",
                      },
                      {
                        file: Torq4,
                        altext: "Fill in the details and click Register",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                </GuidelineStep>
                <GuidelineStep id="connectTorq" title="Connect Torq">
                  <Box width={700}>
                    <TorqConnectForm
                      onSubmit={onSubmit}
                      guideMode={guideMode}
                    />
                  </Box>
                </GuidelineStep>
              </GuidelineStepByStep>
            </div>
          )
        }
      />
    </Box>
  );
};

export default TorqIntegration;
